import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import History from '@Services/History';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import { saveAs } from 'file-saver';
import HeaderContainerService from '@Pages/QueryBuilder/HeaderContainer/HeaderContainerService';
import { getCombinedRouteParams } from '@AppRoutes/AppRoutesSelectors';
import * as AdvancedQuery from '@Services/AdvancedQuery';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import AppStorage from '@Services/AppStorage';
import UserDatabases from '@Services/UserDatabases';
import QueryParser from '@Services/QueryParser';
import store from '@Store';
import HistoryHelper from '@Services/HistoryHelper';
import * as BrowserHelper from '@Services/BrowserHelper';
import Catalyst from '@Services/Catalyst';
import { dispatchOpenImportFileModal } from '@Containers/Search/Shared/ImportFile/ImportFileModalActions';
import { updateImportedQuery } from '../QueryletActions';
import { properties as propertiesSelector } from '../QuerySelector';

export function showOpenStructureEditorMessage() {
  const errorTitle = 'Your query contained reaction attributes.';
  const errorMessage = 'Please open the structure editor to adjust the reaction query as desired.';
  NotifyDialogsService.openModalWindow({
    title: errorTitle,
    text: errorMessage,
    type: 'warning',
    confirmText: 'OK',
    trackingOptions: {
      message: `${errorTitle} ${errorMessage}`,
      component: 'Advanced search',
    },
  });
}

function detectIfNodesHaveValues(nodesLists) {
  eachHierarchy(
    nodesLists,
    (node) => {
      if (node.canExist && !AdvancedQuery.isQueryLetEmpty(node.children)) {
        // eslint-disable-next-line no-param-reassign
        node.openQuerylet = true;
      }
    },
    null,
    {
      children: 'items',
    }
  );
}

function checkIfLegacyReaction(nodes) {
  const structure = find(nodes, { type: 'structure' });

  if (structure && structure.rx1ReactionRole) {
    NotifyDialogsService.openModalWindow({
      text: 'Please note: the role of the substance has to be redefined in new Reaxys using the reaction arrow.',
      type: 'info',
      confirmText: 'OK, got it',
      trackingOptions: {
        component: 'Advanced search',
      },
    });
  }
}

function applyParsedNodes(parsedNodes, nodesList, isSemanticSearchIncluded) {
  nodesList.splice(0, nodesList.length, ...parsedNodes);

  if (isSemanticSearchIncluded) {
    eachHierarchy(
      nodesList,
      (node) => {
        if (node?.type === 'structure') {
          // Mark structure querylets imported from semantic preview edit flow.
          // eslint-disable-next-line no-param-reassign
          node.isSemanticSearchIncluded = true;
        }
      },
      null,
      {
        children: 'items',
      }
    );
  }

  detectIfNodesHaveValues(nodesList);
  checkIfLegacyReaction(nodesList);
}

export function initRootNodesList(nodes, nodesList, properties, isSemanticSearchIncluded) {
  if (nodes) {
    if (nodes.correctedStructureOptions) {
      showOpenStructureEditorMessage();
    }
    applyParsedNodes(QueryParser.parseQuery(nodes, properties), nodesList, isSemanticSearchIncluded);
  }
  store.dispatch(updateImportedQuery(nodesList));
}

export function updateSearchContext(context) {
  let searchDB = AppStorage.instance.get('qbSearchDB');
  const dbs = UserDatabases.getUserSearchContexts();

  if (!searchDB || (searchDB && searchDB.serverAlias !== context)) {
    searchDB = find(dbs, { serverAlias: context });
    AppStorage.instance.set('qbSearchDB', searchDB);
  }
}

export function importer(nodesList, properties, query, { context, resolvedByCasApi, isSemanticSearchIncluded, replaceExistingQuery } = {}) {
  const stateParams = getCombinedRouteParams(store.getState());
  // Keep the imported payload stable while the replace confirmation is open. QueryParser mutates
  // structure wrappers while parsing, so parse the snapshot only after confirmation to make Cancel
  // side-effect-free and prevent later route-state changes from dropping part of a mixed query.
  const querySnapshot = query ? cloneDeep(query) : null;
  const updateQuery = () => {
    const latestProperties = propertiesSelector(store.getState());
    const propertiesForImport = latestProperties?.length ? latestProperties : properties;
    initRootNodesList(querySnapshot, nodesList, propertiesForImport, isSemanticSearchIncluded);
    if (context) {
      updateSearchContext(context);
    }
  };
  if (nodesList.length && !stateParams?.autoSearch) {
    if (resolvedByCasApi) {
      NotifyDialogsService.openModalWindow({
        title: 'Structure search & CAS API service',
        text:
          'CAS Registry Number searches which have been resolved using the CAS API are searched as a structure search. \n\n' +
          'Do you want to replace your form with the selected file?',
        confirmText: 'Replace form',
        cancelText: 'Cancel',
        type: 'info',
        backdrop: 'static',
        onConfirm: updateQuery,
      });
    } else {
      History.openConfirmPopup({
        title: 'Replace form?',
        text: replaceExistingQuery
          ? 'Do you want to replace your current query with the selected search?'
          : 'Do you want to replace your form with the selected file?',
        submitButtonText: 'Yes',
      })
        .then(updateQuery)
        .catch(Logger.error);
    }
  } else {
    if (resolvedByCasApi) {
      NotifyDialogsService.openModalWindow({
        title: 'Structure search & CAS API service',
        text: 'CAS Registry Number searches which have been resolved using the CAS API are searched as a structure search.',
        confirmText: 'Ok',
        type: 'info',
        backdrop: 'static',
      });
    }
    updateQuery();
  }

  if (stateParams && stateParams.autoSearch && nodesList.length) {
    HeaderContainerService.searchInDb(null, nodesList, properties);
  }
}

export function importQuery() {
  // AA tracking - import button
  const sections = [Catalyst.getPrefix(), 'querybuilder', 'import'];
  Catalyst.trackProductFeature(sections.join(':'));

  dispatchOpenImportFileModal();
}

export function saveToFile(name, nodesList) {
  const parser = QueryParser;
  parser.setRemoveEmptyOperators(false);
  const parsedNodeList = parser.stringifyQuery(nodesList);
  const criteria = HistoryHelper.composeFactDescription('Query Builder: ', parsedNodeList);

  if (name) {
    // Save form - download file
    Catalyst.trackSaveSearch({ criteria, type: 'rx2:savesearch:querybuilder:saveform:download' });
  } else {
    // Save query
    Catalyst.trackSaveSearch({ criteria, type: 'rx2:savesearch:querybuilder:save query' });
  }

  const newTab = BrowserHelper.detectSafari() ? window.open() : false;
  AdvancedQuery.saveQueryToFile(parsedNodeList)
    .then((data) => {
      const filename = !name ? data.fileName : `${name}.json`;
      const text = new Blob([JSON.stringify(data)], { type: 'text/plain;charset=utf-8' });
      saveAs(text, filename);
      if (newTab) {
        newTab.alert(
          'To save this file please close this window and press ⌘ + s. ' +
            'In the save dialog use ".json" as file extension and select "Page source" as format.'
        );
      }
    })
    .catch(Logger.error);
}
